import { styled } from '@mui/material/styles';
import { useWindowSize } from 'usehooks-ts';

const GRID_LAYOUT_BREAK_POINTS = {
  MD: 900,
  LG: 1250,
} as const;

type Breakpoints = Lowercase<keyof typeof GRID_LAYOUT_BREAK_POINTS> | 'initial';

type ItemConfiguration = Record<Breakpoints, { columnSpan: number; rowSpan: number }>;

const generateItemStyle = ({ md, lg, initial }: ItemConfiguration) => `
 grid-column: span ${initial.columnSpan};
  grid-row: span ${initial.rowSpan};
   @media (min-width: ${GRID_LAYOUT_BREAK_POINTS.MD}px) {
    grid-column: span ${md.columnSpan};
    grid-row: span ${md.rowSpan};
  }

  @media (min-width: ${GRID_LAYOUT_BREAK_POINTS.LG}px) {
    grid-column: span ${lg.columnSpan};
    grid-row: span ${lg.rowSpan};
  }
`;

const GridLayoutRoot = styled('div')<{ rowHeight: number }>(
  ({ rowHeight, theme }) => `
  display: grid;
  grid-auto-rows: ${Math.round(rowHeight)}px;
  grid-template-columns: repeat(12, 1fr);
  gap: ${theme.spacing(20)};
`,
);

const DEFAULT_BASE_HEIGHT = 768;
const DEFAULT_BASE_ROW_HEIGHT = 212;

type GridLayoutProps = {
  children: React.ReactNode;
  baseHeight?: number;
  baseRowHeight?: number;
};

export const GridLayout = ({
  children,
  baseHeight = DEFAULT_BASE_HEIGHT,
  baseRowHeight = DEFAULT_BASE_ROW_HEIGHT,
}: GridLayoutProps) => {
  const { height = 0 } = useWindowSize();
  return (
    <GridLayoutRoot rowHeight={baseRowHeight * Math.max(1, height / baseHeight)}>
      {children}
    </GridLayoutRoot>
  );
};

export const GridSmallItem = styled('div')(
  generateItemStyle({
    initial: {
      columnSpan: 12,
      rowSpan: 2,
    },
    md: {
      columnSpan: 6,
      rowSpan: 1,
    },
    lg: {
      columnSpan: 2,
      rowSpan: 1,
    },
  }),
);

export const GridLayoutItem = styled('div')(
  generateItemStyle({
    initial: {
      columnSpan: 12,
      rowSpan: 2,
    },
    md: {
      columnSpan: 6,
      rowSpan: 1,
    },
    lg: {
      columnSpan: 4,
      rowSpan: 1,
    },
  }),
);

export const GridLayoutHighItem = styled('div')(
  generateItemStyle({
    initial: {
      columnSpan: 12,
      rowSpan: 2,
    },
    md: {
      columnSpan: 6,
      rowSpan: 2,
    },
    lg: {
      columnSpan: 4,
      rowSpan: 2,
    },
  }),
);

export const GridLayoutHalfWidthItem = styled('div')(
  generateItemStyle({
    initial: {
      columnSpan: 12,
      rowSpan: 2,
    },
    md: {
      columnSpan: 8,
      rowSpan: 1,
    },
    lg: {
      columnSpan: 6,
      rowSpan: 1,
    },
  }),
);

export const GridLayoutWideItem = styled('div')(
  generateItemStyle({
    initial: {
      columnSpan: 12,
      rowSpan: 2,
    },
    md: {
      columnSpan: 6,
      rowSpan: 1,
    },
    lg: {
      columnSpan: 8,
      rowSpan: 1,
    },
  }),
);

export const GridLayoutFullWidthItem = styled('div')(
  generateItemStyle({
    initial: {
      columnSpan: 12,
      rowSpan: 2,
    },
    md: {
      columnSpan: 12,
      rowSpan: 1,
    },
    lg: {
      columnSpan: 12,
      rowSpan: 1,
    },
  }),
);
